/**
 * Main layout component with navigation
 */
import React, { useState } from 'react';
import {
  Menu,
  LayoutDashboard,
  List,
  Upload,
  Mail,
  LogOut,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useAuthStore } from '@/store/authStore';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface LayoutProps {
  children: React.ReactNode;
  darkMode: boolean;
  onToggleDarkMode: () => void;
}

const drawerWidth = 240;

const menuItems = [
  { text: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { text: 'Vouchers', icon: List, path: '/vouchers' },
  { text: 'Upload', icon: Upload, path: '/upload' },
  { text: 'Gmail Connections', icon: Mail, path: '/gmail' },
];

export const Layout: React.FC<LayoutProps> = ({ children, darkMode, onToggleDarkMode }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = !useMediaQuery('(min-width: 768px)');
  const { logout, user } = useAuthStore();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navContent = (
    <>
      <div className="flex h-14 items-center px-4">
        <span className="text-lg font-semibold">Voucher Tracker</span>
      </div>
      <nav className="flex flex-1 flex-col gap-1 px-2">
        {menuItems.map((item) => {
          const active = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <Button
              key={item.text}
              variant={active ? 'secondary' : 'ghost'}
              className="justify-start gap-3"
              onClick={() => handleNavigation(item.path)}
            >
              <Icon className="size-5" />
              {item.text}
            </Button>
          );
        })}
      </nav>
      <div className="border-t p-4">
        <div className="mb-3 flex items-center gap-2">
          <Switch
            id="dark-mode"
            checked={darkMode}
            onCheckedChange={onToggleDarkMode}
          />
          <Label htmlFor="dark-mode" className="text-sm">
            {darkMode ? 'Dark' : 'Light'}
          </Label>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start gap-3"
          onClick={handleLogout}
        >
          <LogOut className="size-5" />
          Logout
        </Button>
        {user?.email && (
          <p className="mt-2 px-2 text-xs text-muted-foreground">{user.email}</p>
        )}
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen">
      {/* App bar */}
      <header
        className="fixed top-0 z-40 flex h-14 w-full items-center border-b bg-background px-4 md:ml-[240px] md:w-[calc(100%-240px)]"
      >
        <Button
          variant="ghost"
          size="icon"
          className="mr-2 md:hidden"
          onClick={handleDrawerToggle}
          aria-label="Open menu"
        >
          <Menu className="size-5" />
        </Button>
        <span className="text-lg font-semibold">Voucher Tracker</span>
      </header>

      {/* Mobile sheet */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[240px] p-0">
          <SheetHeader className="sr-only">
            <SheetTitle>Menu</SheetTitle>
          </SheetHeader>
          <div className="flex h-full flex-col pt-4">{navContent}</div>
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar */}
      <aside
        className="fixed left-0 top-0 z-30 hidden h-full w-[240px] flex-col border-r bg-card md:flex"
        style={{ width: drawerWidth }}
      >
        {navContent}
      </aside>

      {/* Main content */}
      <main className="min-w-0 flex-1 pt-14 md:pl-[240px]">
        <div className="p-6">{children}</div>
      </main>
    </div>
  );
};
